import { useEffect, useRef, useState } from "react";
import { api } from "./api.js";
import "./landing.css";
import "./landing-v2.css";
import "./landing-v3.css";

// Гостьова сторінка: спочатку продукт і шлях початку роботи, потім деталі.
// Вхід та реєстрація — окремий доступний діалог; компанію підключають після створення акаунта.
// Переходи між розділами — прокруткою, без зміни hash (hash зайнятий маршрутами застосунку).

const reduceMotion = () => matchMedia("(prefers-reduced-motion: reduce)").matches;
const scrollToId = (id, block = "start") => document.getElementById(id)?.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block });

// Тематичні секції (порядок — рішення користувача 23.10.2026): ПН з Оракулом → декларація з ПДВ → СМКОР → митні
// декларації → комплаєнс. Знімки — справжній інтерфейс на демо-компанії, знеособлені (tools/visual/landing-shots.mjs)
const SECTIONS = [
  { id: "pn", kicker: "Податкові накладні", title: "ПН — за чотири кроки.", accent: "З перевіркою до подання.", img: "/lp/pn.jpg", overlay: "/lp/oracle.jpg", overlayOn: 1,
    slides: ["/lp/pn-step1.jpg", "/lp/pn-step2.jpg", "/lp/pn-step3.jpg", "/lp/pn-step4.jpg"],
    steps: [["Операція й покупець", "з даних компанії та ЄДР", "user"], ["Товари та послуги", "з видаткової чи акта", "box"], ["Перевірка", "Оракул: ризики СМКОР", "shield"], ["Підпис і реєстрація", "КЕП у браузері → ЄРПН", "pen"]],
    lead: "Продавець — з даних компанії, покупець — з ваших ПН, контрагентів або ЄДР. Позиції — з видаткової, акта чи рахунку.",
    points: ["Оракул перевіряє ризики реєстрації за доступними даними та правилами СМКОР — остаточний результат визначає ДПС", "Залишок за кодами УКТЗЕД, ліміти й ознаки безумовної реєстрації — на одному екрані", "Підпис ключем у браузері й реєстрація в ЄРПН — без інших програм"] },
  { id: "vat", kicker: "Звітність", title: "Декларація з ПДВ.", accent: "Без ручного перенесення.", img: "/lp/vat.jpg",
    steps: [["Місяць", "дані ДПС і ЄРПН", "calendar"], ["Операції", "зупинені ПН і кредит", "list"], ["Результат", "до сплати чи відшкодування", "chart"], ["Перегляд", "бланк, підпис, квитанції", "pen"]],
    lead: "Дані — з кабінету ДПС і ЄРПН. Ви лише вирішуєте спірне: які зупинені ПН і вхідні включати.",
    points: ["ПДВ з продажів, кредит і перенесення рахуються самі", "Бланк затвердженої форми перед підписом і перевірка за схемою ДПС", "Квитанції приходять самі — без «оновити» й очікування"] },
  { id: "risk", kicker: "СМКОР", title: "Матриця ризиковості.", accent: "Зрозумійте, що перевірити.", img: "/lp/risk.jpg",
    lead: "Усі 20 кодів податкової інформації й критерії платника: де ви зараз за даними кабінету ДПС.",
    points: ["Ознаки за кожним кодом, пояснення та прогалини в даних", "Ресурси, працівники, склад і залишки — у контексті вашої діяльності", "Наступні дії для перевірки; офіційний статус партнерів — за їхньою згодою"] },
  { id: "customs", kicker: "Митні декларації", title: "Імпорт — з митниці.", accent: "І звірено з СЕА ПДВ.", img: "/lp/customs-md.jpg", overlay: "/lp/customs.jpg", overlayAlt: "Перелік митних декларацій: номер, тип, дата оформлення",
    lead: "МД надходять з митниці за токеном — без файлів .imfx. Кожну звіряємо з реєстром операцій СЕА ПДВ.",
    points: ["Митна вартість, мито й ПДВ за кожною МД", "Позначка «у СЕА»: чи врахована МД у ліміті реєстрації", "Товари з МД — у русі товарів і залишках для Оракула"] },
  { id: "compliance", kicker: "Комплаєнс", title: "Позитивна податкова історія.", accent: "З розрахунком.", img: "/lp/compliance.jpg",
    lead: "Які ознаки позитивної історії вже виконані, яких бракує і що реально змінити, щоб ПН не зупиняли.",
    points: ["Ознаки — за висновком кабінету ДПС і нашим розрахунком", "Ліміт 500 000 і що його знімає", "Територія високої податкової довіри — оцінка за періодами"] },
];

// Решта можливостей — плитками
const TILES = [
  { title: "Бізнес-партнери.", text: "Взаємна згода в кабінеті ДПС одним підписом — і ви бачите офіційний статус ризиковості, зупинені ПН і борг контрагента.", tag: "Контрагенти", big: true, art: "partners" },
  { title: "Зупинені ПН — до розблокування.", text: "Пояснення й доповнення (J1312603, J1307801), рішення комісії, повідомлення про невідповідність критеріям.", tag: "Розблокування", art: "oracle" },
  { title: "Первинка й рух товарів.", text: "Рахунки, видаткові, акти; залишки за УКТЗЕД і СЕА ПДВ.", tag: "Аналітика", art: "flow" },
  { title: "Кілька компаній — один вхід.", text: "Перегляд, підготовка, подання, власник: кожному рівно стільки доступу, скільки потрібно.", tag: "Компанії", big: true, art: "roles" },
];

const TRUST = [
  { title: "Ключ не залишає ваш пристрій.", text: "Підпис КЕП створюється в браузері. Сервер перевіряє підпис, але ніколи не бачить ні ключа, ні пароля." },
  { title: "Напряму з кабінетом ДПС.", text: "Дані надходять з Електронного кабінету платника за вашою авторизацією — без посередників і ручного перенесення." },
  { title: "Кожна компанія — окремо.", text: "Дані підприємств ізольовані на рівні бази даних, сесії ДПС зберігаються лише зашифрованими." },
];

export default function Landing({ onLogin }) {
  const [authMode, setAuthMode] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planFailed, setPlanFailed] = useState(false);
  const goLogin = () => setAuthMode("login");
  const goRegister = () => setAuthMode("register");
  useEffect(() => {
    let active = true;
    api("/api/public/plans").then((p) => { if (active) setPlan(p); }, () => { if (active) setPlanFailed(true); });
    return () => { active = false; };
  }, []);
  useReveal();
  return (
    <div className="lp lp-v2">
      <a className="lp-skip" href="#landing-main">До основного вмісту</a>
      <header className="lp-bar">
        <div className="lp-wrap lp-bar-row">
          <a className="lp-brand" href="/" aria-label="eCabinet — на головну"><img src="/logo.png" width="22" height="22" alt="" />eCabinet</a>
          <nav className="lp-nav" aria-label="Розділи">
            <button type="button" onClick={() => scrollToId("pn")}>Можливості</button>
            <button type="button" onClick={() => scrollToId("start")}>Як почати</button>
            <button type="button" onClick={() => scrollToId("security")}>Безпека</button>
            <button type="button" onClick={() => scrollToId("pricing")}>Тариф</button>
          </nav>
          <div className="lp-bar-actions">
            <button type="button" className="lp-login-link" onClick={goLogin}>Увійти</button>
            <button type="button" className="lp-bar-cta" onClick={goRegister}>Створити акаунт</button>
          </div>
        </div>
      </header>

      <main id="landing-main" tabIndex={-1}>
        <section className="lp-hero3" aria-labelledby="hero-title">
          <div className="lp-wrap lp-hero3-grid">
            <div className="lp-hero3-copy">
              <p className="lp-eyebrow3"><span className="sr-only">Простір сучасного бізнесу</span><span className="lp-eyebrow3-line" aria-hidden="true" />{["Простір", "сучасного", "бізнесу"].map((w, i) => <span key={w} className="lp-eyebrow3-w" aria-hidden="true" style={{ "--i": i }}>{w}</span>)}</p>
              <h1 id="hero-title" className="lp-hero3-title">Податки.<br /><span>Під вашим контролем.</span></h1>
              <p className="lp-hero3-lead">ПН з перевіркою ризиків до подання, звітність без ручного перенесення і дані кабінету ДПС — в одному робочому просторі.</p>
              <div className="lp-hero3-actions">
                <button type="button" className="lp-btn3" onClick={goRegister}>Створити акаунт <span aria-hidden="true">→</span></button>
                <button type="button" className="lp-ghost3" onClick={() => scrollToId("pn")}>Подивитися можливості</button>
              </div>
              <ul className="lp-hero3-proof"><li>ПН / РК та ЄРПН</li><li>Матриця СМКОР</li><li>КЕП у браузері</li></ul>
            </div>
            <Stack3D />
          </div>
        </section>

        <section className="lp-start3" id="start" aria-labelledby="start-title">
          <div className="lp-wrap">
            <div className="lp-start-head lp-reveal"><p className="lp-kicker">Як почати</p><h2 id="start-title" className="lp-h2">Три кроки до роботи.</h2></div>
            <ol className="lp-glass-steps">
              {START.map((x, i) => (
                <li key={x.title} className="lp-glass lp-reveal" style={{ "--i": i }}>
                  <span className="lp-glass-num">0{i + 1}</span>
                  <span className="lp-glass-icon" aria-hidden="true"><Ico name={x.icon} /></span>
                  <h3>{x.title}</h3><p>{x.text}</p>
                </li>))}
            </ol>
          </div>
        </section>

        {SECTIONS.map((x, i) => <Feature key={x.id} f={x} flip={i % 2 === 1} />)}

        <section className="lp-sec" id="features">
          <div className="lp-wrap">
            <div className="lp-head lp-reveal">
              <h2 className="lp-h2">І ще.<br /><span>Усе, що потрібно щомісяця.</span></h2>
            </div>
            <div className="lp-bento">
              {TILES.map((t) => (
                <article key={t.title} className={`lp-tile lp-reveal${t.big ? " big" : ""}`}>
                  <p className="lp-tile-tag">{t.tag}</p>
                  <h3>{t.title}</h3>
                  <p>{t.text}</p>
                  <TileArt kind={t.art} />
                </article>
              ))}
            </div>
            <p className="lp-soon lp-reveal">Незабаром: банківські виписки через API і звірка оплат із податковими накладними.</p>
          </div>
        </section>

        <section className="lp-sec lp-sec-dark" id="security">
          <div className="lp-wrap">
            <div className="lp-head lp-reveal">
              <p className="lp-kicker">Безпека</p>
              <h2 className="lp-h2">Довіра закладена<br />в архітектуру.</h2>
            </div>
            <div className="lp-trust">
              {TRUST.map((t) => (
                <div key={t.title} className="lp-trust-item lp-reveal"><h3>{t.title}</h3><p>{t.text}</p></div>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-sec" id="pricing">
          <div className="lp-wrap lp-reveal">
            <div className="lp-price-head"><p className="lp-kicker">Тариф</p>
              <h2 className="lp-h2">Два тарифи.<br /><span>Усе включено.</span></h2>
              <p className="lp-lead">Платите за кожну підключену компанію. Оплата карткою або за рахунком — він виписується автоматично, з актом для бухгалтерії.</p>
            </div>
            <div className="lp-price-plans">
              {[["Місяць", plan?.price || 720, "грн / місяць", null, false], ["Рік", plan?.priceYear || 6900, "грн / рік", yearSave(plan), true]].map(([name, sum, per, note, dark]) => (
                <div key={name} className={`lp-price-card3${dark ? " dark" : ""}`}>
                  <p className="lp-price-label">{name} · за компанію{note && <span className="lp-price-save">{note}</span>}</p>
                  <p className="lp-price-big">{new Intl.NumberFormat("uk-UA").format(sum)} <span>{per}</span></p>
                  <ul className="lp-price-list">{["ПН і РК з Оракулом", "Декларація з ПДВ, ПДФО / ВЗ / ЄСВ, фінзвітність", "Матриця ризиковості СМКОР і розблокування ПН", "Бізнес-партнери, митні декларації, комплаєнс"].map((x) => <li key={x}>{x}</li>)}</ul>
                  <button type="button" className="lp-btn3 lp-btn-block" onClick={goRegister}>Створити акаунт <span aria-hidden="true">→</span></button>
                </div>))}
            </div>
          </div>
        </section>

        <section className="lp-final">
          <div className="lp-wrap lp-reveal">
            <p className="lp-kicker">Ваш наступний крок</p>
            <h2 className="lp-h2">Менше перенесення даних.<br /><span>Більше контролю.</span></h2>
            <p className="lp-lead">Створіть акаунт і підключіть першу компанію. Далі — ваші документи, перевірки й звітність в одному просторі.</p>
            <button type="button" className="lp-btn" onClick={goRegister}>Створити акаунт <span aria-hidden="true">→</span></button>
            <button type="button" className="lp-text-action" onClick={goLogin}>Вже маєте акаунт? Увійти</button>
          </div>
        </section>
      </main>

      <footer className="lp-foot">
        <div className="lp-wrap lp-foot-row">
          <span>eCabinet — простір сучасного бізнесу</span>
          <span>© {new Date().getFullYear()}</span>
        </div>
      </footer>
      {authMode && <AuthDialog mode={authMode} onMode={setAuthMode} onClose={() => setAuthMode(null)} onLogin={onLogin} />}
    </div>
  );
}

function AuthDialog({ mode, onMode, onClose, onLogin }) {
  const dialog = useRef(null);
  const firstField = useRef(null);
  const [email, setEmail] = useState(""), [password, setPassword] = useState(""), [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null), [busy, setBusy] = useState(false);
  const register = mode === "register";
  useEffect(() => { dialog.current.showModal(); }, []);
  useEffect(() => { firstField.current?.focus(); }, [mode]);
  const switchMode = () => { setError(null); setPassword(""); setShowPassword(false); onMode(register ? "login" : "register"); };
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError(null); setBusy(true);
    try {
      if (register) {
        const result = await api("/api/auth/register", { method: "POST", body: { email: email.trim(), password, name: name.trim() } });
        // Invite links retain their destination; a new user otherwise starts with KEP onboarding.
        if (!location.hash.startsWith("#/invite/")) location.hash = "#/add";
        onLogin(result.user);
      } else {
        await api("/api/auth/login", { method: "POST", body: { email: email.trim(), password } });
        onLogin((await api("/api/auth/me")).user);
      }
    }
    catch (err) { setError(err.message); setBusy(false); }
  };
  return (
    <dialog ref={dialog} className="lp-auth-dialog" aria-labelledby="auth-title" aria-describedby="auth-description"
      onClose={onClose} onCancel={(e) => { if (busy) e.preventDefault(); }}>
      <button type="button" className="lp-dialog-close" onClick={() => dialog.current.close()} disabled={busy} aria-label="Закрити форму">×</button>
      <p className="lp-kicker">eCabinet</p>
      <h2 id="auth-title">{register ? "Почнімо з вашого акаунта." : "Раді бачити вас знову."}</h2>
      <p id="auth-description" className="lp-auth-description">{register ? "Створіть обліковий запис. Далі підключіть компанію ключем КЕП." : "Увійдіть, щоб продовжити роботу зі своїми компаніями."}</p>
      <form onSubmit={submit} className="lp-auth-form" aria-busy={busy}>
        <fieldset disabled={busy}>
          {register && <label className="lp-auth-field">Ваше ім’я <span className="lp-optional">необов’язково</span>
            <input ref={firstField} name="name" autoComplete="name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></label>}
          <label className="lp-auth-field">Email
            <input ref={register ? null : firstField} type="email" name="email" autoComplete="username" autoCapitalize="none" spellCheck={false} value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} required /></label>
          <div className="lp-auth-field"><label htmlFor="auth-password">Пароль</label>
            <span className="lp-password-wrap"><input id="auth-password" type={showPassword ? "text" : "password"} name="password" autoComplete={register ? "new-password" : "current-password"}
              value={password} minLength={register ? 12 : undefined} maxLength={200} aria-describedby={register ? "password-hint" : undefined} onChange={(e) => setPassword(e.target.value)} required />
              <button type="button" className="lp-password-toggle" aria-label={showPassword ? "Приховати пароль" : "Показати пароль"} aria-pressed={showPassword} onClick={() => setShowPassword((v) => !v)}>{showPassword ? "Сховати" : "Показати"}</button></span>
          </div>
          {register && <p id="password-hint" className="lp-auth-hint">Від 12 символів. Можна використати довгу фразу.</p>}
          {error && <p className="lp-auth-error" role="alert">{error}</p>}
          <button type="submit" className="lp-btn lp-btn-block">{busy ? (register ? "Створюємо акаунт…" : "Входимо…") : register ? "Створити акаунт" : "Увійти"}</button>
        </fieldset>
      </form>
      <p className="lp-auth-switch">{register ? "Вже маєте акаунт?" : "Вперше в eCabinet?"} <button type="button" onClick={switchMode} disabled={busy}>{register ? "Увійти" : "Зареєструватися"}</button></p>
      <p className="lp-auth-footnote">Це обліковий запис eCabinet. Ключ КЕП та пароль до нього для реєстрації не потрібні.</p>
    </dialog>
  );
}

// Невеликі ілюстрації плиток — CSS-форми без даних
function TileArt({ kind }) {
  if (kind === "report") return (
    <div className="art art-report"><div className="art-steps">{["Дані", "Перевірка", "Бланк", "Підпис", "Квитанція"].map((s, i) => <span key={s} className={i < 4 ? "done" : "on"}><i>{i < 4 ? "✓" : i + 1}</i>{s}</span>)}</div>
      <div className="art-sum"><span><em /><b /></span><span><em /><b /></span><span className="hl"><em /><b /></span></div></div>);
  if (kind === "pn") return <div className="art art-list">{[78, 62, 70].map((w, i) => <span key={i}><em style={{ width: `${w}%` }} /><i className={i === 1 ? "warn" : ""} /></span>)}</div>;
  if (kind === "oracle") return <div className="art art-ring"><svg viewBox="0 0 120 120"><circle cx="60" cy="60" r="48" className="trk" /><circle cx="60" cy="60" r="48" className="val" /></svg><b>✓</b></div>;
  if (kind === "partners") return <div className="art art-chips">{["Платник ПДВ", "На обліку", "Без боргу"].map((c) => <span key={c}>{c}</span>)}</div>;
  if (kind === "marks") return <div className="art art-marks">{[1, 1, 0, 1, 0, 1, 1, 0, 1].map((ok, i) => <span key={i} className={ok ? "ok" : ""}>{i + 1}</span>)}</div>;
  if (kind === "roles") return <div className="art art-roles">{["Власник", "Подання", "Підготовка", "Перегляд"].map((r, i) => <span key={r}><i className={`r${i}`} />{r}</span>)}</div>;
  return <div className="art art-bars">{[40, 64, 52, 80, 70, 92, 76].map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}</div>;
}

// Знімок екрана — «глянцево»: без рамки браузера, заокруглений, з м'якою тінню
function Shot({ src, alt }) {
  // Завантажуємо одразу: «ліниві» знімки в далеких секціях подекуди лишались порожніми
  return <img className="lp-shot" src={src} alt={alt} width="1176" height="760" loading="eager" decoding="async" />;
}

// Тематична секція: текст і знімок поряд (через одну — дзеркально); кроки майстра — степер з лінією прогресу й
// почерговим підсвічуванням; накладка — картка поверх знімка
function Feature({ f, flip }) {
  const n = f.slides?.length || 0, [cur, setCur] = useState(0), [prev, setPrev] = useState(-1), [hold, setHold] = useState(false);
  const [back, setBack] = useState(false), swipe = useRef(null);
  // Напрям — як на iPhone: уперед нова сторінка заїжджає справа, назад — зліва
  const go = (i, dir) => { if (i === cur) return; setBack(dir === undefined ? i < cur : dir < 0); setPrev(cur); setCur(i); };
  const step = (d) => go((cur + d + n) % n, d);
  // Сторінки майстра гортаються самі раз на 5 с (пауза під курсором); без руху за prefers-reduced-motion
  useEffect(() => {
    if (!n || hold || reduceMotion()) return;
    const t = setTimeout(() => { if (!document.hidden) step(1); }, 5000);
    return () => clearTimeout(t);
  }, [cur, hold, n]);
  const showOverlay = f.overlay && (!n || f.overlayOn === undefined || f.overlayOn === cur);
  return (
    <section className={`lp-sec lp-feature${flip ? " flip" : ""}`} id={f.id}>
      <div className="lp-wrap lp-feature-in">
        <div className="lp-feature-copy lp-reveal">
          <p className="lp-kicker">{f.kicker}</p>
          <h2 className="lp-h2">{f.title}<br /><span>{f.accent}</span></h2>
          <p className="lp-lead">{f.lead}</p>
          {f.steps && <ol className={`lp-stepper${n ? " live" : ""}`} style={{ "--n": f.steps.length, "--p": n ? cur / (n - 1) : 1 }}>{f.steps.map(([t, d, icon], i) => {
            const body = <><span className="lp-stepper-dot" aria-hidden="true"><Ico name={icon} /></span><b>{t}</b><small>{d}</small></>;
            return <li key={t} style={{ "--i": i }} className={n ? (i === cur ? "on" : i < cur ? "done" : "") : undefined}>
              {n ? <button type="button" onClick={() => go(i)} aria-pressed={i === cur}>{body}</button> : body}</li>;
          })}</ol>}
          <ul className="lp-points">{f.points.map((p) => <li key={p}>{p}</li>)}</ul>
        </div>
        <div className="lp-feature-art lp-reveal" onPointerEnter={() => setHold(true)} onPointerLeave={() => setHold(false)}>
          {n ? (
            // Рівні сторінки без нахилу, гортання як на iPhone: нова заїжджає збоку, попередня відходить із паралаксом і
            // темніє; свайп пальцем або мишею — вперед/назад, клік — наступна
            <button type="button" className={`lp-pages${back ? " back" : ""}`} aria-label={`Крок ${cur + 1} з ${n}: ${f.steps[cur][0]}. Показати наступний`}
              onPointerDown={(e) => { swipe.current = { x: e.clientX, done: false }; }}
              onPointerUp={(e) => { const sw = swipe.current; if (sw && Math.abs(e.clientX - sw.x) > 40) { sw.done = true; step(e.clientX > sw.x ? -1 : 1); } }}
              onClick={() => { if (swipe.current?.done) { swipe.current = null; return; } swipe.current = null; step(1); }}>
              {f.slides.map((src, i) => <img key={src} src={src} alt="" loading="eager" decoding="async" draggable="false"
                className={i === cur ? (prev >= 0 ? "on in" : "on") : i === prev ? "out" : ""} />)}
            </button>
          ) : <Shot src={f.img} alt={`eCabinet — ${f.kicker}: ${f.title}`} />}
          {showOverlay && <img key={cur} className="lp-overlay" src={f.overlay} alt={f.overlayAlt || "Оракул: перевірені умови, залишки й висновок"} loading="eager" decoding="async" />}
        </div>
      </div>
    </section>
  );
}

// Річний тариф: економія проти 12 місяців помісячно
const yearSave = (p) => { const m = (p?.price || 720) * 12 - (p?.priceYear || 6900); return m > 0 ? `−${new Intl.NumberFormat("uk-UA").format(m)} грн` : null; };

// Головний екран: справжні екрани застосунку з меню розділів (активний розділ підсвічено) у 3D-стосі; клік або кожні 30 с — наступний:
// передній відлітає вгору й стає в кінець стосу, у меню наступного видно, як перемкнувся розділ. Крапки — перейти до екрана
// [знімок, розділ, опис для читача екрана, дві плашки під цей екран: [значок, заголовок, підпис]] — цифри з демо-компанії на знімку
const STACK = [
  ["hero-overview", "Огляд", "податкові накладні, ліміт реєстрації, розрахунки з бюджетом і що потребує уваги",
    [["✓", "Ризиків не виявлено", "Оракул · за доступними даними"], ["₴", "Ліміт реєстрації 55 400 грн", "СЕА ПДВ"]]],
  ["hero-stock", "Залишки", "залишки товарів з ПДВ за УКТЗЕД і скільки ще можна продати за кожною групою без критерію 1 СМКОР",
    [["✓", "Доступно 927 250 грн", "група 8471 · критерій 1 СМКОР"], ["₴", "Залишки з ПДВ 826 200 грн", "за УКТЗЕД, з МД і СЕА"]]],
  ["hero-pn", "ПН/РК", "майстер податкової накладної: товари й підсумок",
    [["✓", "Помилок немає", "перевірено до підпису"], ["₴", "ПН № 48 · 4 800 грн", "зареєструвати до 18.11.2026"]]],
  ["hero-oracle", "Оракул", "прогноз реєстрації ПН до підпису: критерії СМКОР і залишок за кодами УКТ ЗЕД",
    [["✓", "Критерій 1 не спрацює", "залишку вистачає за всіма кодами"], ["₴", "Залишок 184 000 грн", "група УКТ ЗЕД 9403"]]],
  ["hero-reports", "Звітність", "декларація з ПДВ: операції за місяць і рішення щодо зупинених ПН",
    [["✓", "Дані з ЄРПН і СЕА", "без ручного перенесення"], ["₴", "ПДВ з продажів 20 000 грн", "декларація за вересень"]]],
  ["hero-smkor", "СМКОР", "статус ризиковості й що робити, щоб розблокувати ПН",
    [["✓", "Не ризиковий платник", "критеріїв у кабінеті немає"], ["!", "4 зупинені ПН", "план розблокування готовий"]]],
  ["hero-partners", "Партнери", "бізнес-партнери: взаємна згода в кабінеті ДПС і офіційні відомості одне про одного",
    [["✓", "Партнер підтвердив згоду", "взаємно, у кабінеті ДПС"], ["⇄", "Статус СМКОР партнерів", "офіційно, з кабінету ДПС"]]],
  ["hero-compliance", "Комплаєнс", "позитивна податкова історія й ознаки ДПС",
    [["✓", "Позитивна історія", "діє ознака 5"], ["★", "Податкова довіра", "що заважає включенню"]]],
];
function Stack3D() {
  const ref = useRef(null), navRef = useRef(null), [top, setTop] = useState(0), [prev, setPrev] = useState(-1);
  // Перемикач на вузькому екрані гортається вбік — поточний розділ тримаємо видимим (без прокрутки сторінки)
  useEffect(() => {
    const n = navRef.current, b = n?.children[top];
    if (n && b && n.scrollWidth > n.clientWidth) n.scrollTo({ left: b.offsetLeft - (n.clientWidth - b.offsetWidth) / 2, behavior: reduceMotion() ? "auto" : "smooth" });
  }, [top]);
  const go = (i) => { setPrev(top); setTop((i + STACK.length) % STACK.length); };
  // Самі гортаються раз на 30 с (після кліку відлік — заново); не у фоновій вкладці й без руху за prefers-reduced-motion
  useEffect(() => {
    if (reduceMotion()) return;
    const t = setTimeout(() => { if (!document.hidden) go(top + 1); }, 30000);
    return () => clearTimeout(t);
  }, [top]);
  useEffect(() => {
    const el = ref.current;
    if (!el || reduceMotion() || matchMedia("(pointer: coarse)").matches) return;
    const move = (e) => {
      const r = el.getBoundingClientRect(), x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty("--rx", `${(-y * 6).toFixed(2)}deg`); el.style.setProperty("--ry", `${(x * 8).toFixed(2)}deg`);
    };
    const leave = () => { el.style.setProperty("--rx", "0deg"); el.style.setProperty("--ry", "0deg"); };
    el.addEventListener("pointermove", move); el.addEventListener("pointerleave", leave);
    return () => { el.removeEventListener("pointermove", move); el.removeEventListener("pointerleave", leave); };
  }, []);
  const next = STACK[(top + 1) % STACK.length];
  return (
    <div className="lp-stack" ref={ref}>
      <button type="button" className="lp-stack-in" onClick={() => go(top + 1)} aria-label={`Екран «${STACK[top][1]}»: ${STACK[top][2]}. Показати наступний — «${next[1]}»`}>
        {STACK.map(([name], i) => {
          const pos = (i - top + STACK.length) % STACK.length;
          return <img key={name} className={`lp-stack-card p${pos}${i === prev && i !== top ? " to-back" : ""}`} src={`/lp/${name}.jpg`} alt="" width="1280" height="760"
            loading="eager" decoding="async" fetchPriority={i === 0 ? "high" : undefined} />;
        })}
      </button>
      <div className="lp-stack-nav" ref={navRef}>
        {STACK.map(([name, title], i) => <button key={name} type="button" className={i === top ? "on" : ""} aria-pressed={i === top} onClick={() => i !== top && go(i)}>{title}</button>)}
      </div>
      {STACK[top][3].map(([ic, b, sm], k) => (
        <div key={`${top}-${k}`} className={`lp-chip3 k${k + 1}`} aria-hidden="true"><i>{ic}</i><span><b>{b}</b><small>{sm}</small></span></div>))}
    </div>
  );
}

// Іконки кроків (контурні, як у застосунку)
const ICONS = {
  user: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.8-3.5 3.6-5.5 7-5.5s6.2 2 7 5.5" /></>,
  key: <><circle cx="8" cy="15" r="4" /><path d="m11 12 8-8M16 7l2.5 2.5M14 9l1.8 1.8" /></>,
  chart: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
  box: <><path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5z" /><path d="m3 7.5 9 4.5 9-4.5M12 12v9" /></>,
  shield: <><path d="M12 3 20 6v6c0 4.5-3.4 7.6-8 9-4.6-1.4-8-4.5-8-9V6z" /><path d="m8.5 12 2.5 2.5 4.5-5" /></>,
  pen: <><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="m13.5 6.5 4 4" /></>,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  list: <><path d="M9 6h12M9 12h12M9 18h12" /><circle cx="4.5" cy="6" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="18" r="1" /></>,
};
const Ico = ({ name }) => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{ICONS[name]}</svg>;

const START = [
  { icon: "user", title: "Створіть акаунт", text: "Email і пароль — ваш вхід до eCabinet. Компанію оберете наступним кроком." },
  { icon: "key", title: "Підключіть компанію", text: "Авторизуйтеся її ключем КЕП. Файл ключа й пароль залишаються у вашому браузері." },
  { icon: "chart", title: "Працюйте з даними", text: "Синхронізація з кабінетом ДПС — і перша ПН чи звіт за кілька хвилин." },
];

// Поява блоків під час прокрутки; без IntersectionObserver або з reduced-motion — одразу видимі
function useReveal() {
  useEffect(() => {
    const els = [...document.querySelectorAll(".lp-reveal")];
    if (reduceMotion() || !("IntersectionObserver" in window)) { els.forEach((el) => el.classList.add("is-visible")); return; }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); }
    }, { rootMargin: "0px 0px -8% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}
